import { useState, useRef, useCallback, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Button } from "../../../components/ui/button";
import { Menu, X, ChevronRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

interface PublicNavigationProps {
  showRecruiterLink?: boolean;
  onRecruiterClick?: () => void;
}

const navSections = [
  {
    id: "roles",
    label: "Prep by Role",
    links: [
      {
        label: "Software Engineer",
        description: "Coding, system design & technical interviews",
        path: "/software-engineer-interview-prep",
      },
      {
        label: "Data Scientist",
        description: "ML concepts, statistics & case studies",
        path: "/data-scientist-interview-prep",
      },
      {
        label: "Product Manager",
        description: "Strategy, metrics & product sense questions",
        path: "/product-manager-interview-prep",
      },
      {
        label: "Consultant",
        description: "Case interviews & frameworks",
        path: "/consultant-interview-prep",
      },
      {
        label: "Financial Analyst",
        description: "Modeling, valuation & technical finance",
        path: "/financial-analyst-interview-prep",
      },
      {
        label: "Marketing Manager",
        description: "Campaign strategy & analytics questions",
        path: "/marketing-manager-interview-prep",
      },
    ],
  },
  {
    id: "types",
    label: "Interview Types",
    links: [
      {
        label: "Coding Interview",
        description: "Algorithms, data structures & live coding",
        path: "/coding-interview-practice",
      },
      {
        label: "Case Interview",
        description: "Business problems & structured frameworks",
        path: "/case-interview-preparation",
      },
      {
        label: "Technical Interview",
        description: "Deep-dive technical knowledge assessment",
        path: "/technical-interview-simulator",
      },
      {
        label: "Behavioral Interview",
        description: "STAR method & leadership questions",
        path: "/behavioral-interview-coach",
      },
      {
        label: "System Design",
        description: "Architecture & scalability challenges",
        path: "/system-design-interview-prep",
      },
    ],
  },
  {
    id: "compare",
    label: "Compare",
    links: [
      {
        label: "OnSite vs LeetCode",
        description: "Full interview simulation vs problem grinding",
        path: "/vs/leetcode",
      },
      {
        label: "OnSite vs Pramp",
        description: "On-demand vs peer-to-peer practice",
        path: "/vs/pramp",
      },
      {
        label: "OnSite vs Interviewing.io",
        description: "Always-available vs scheduled sessions",
        path: "/vs/interviewing-io",
      },
      {
        label: "All Alternatives",
        description: "Compare the top interview prep platforms",
        path: "/alternatives/interview-prep-platforms",
      },
    ],
  },
];

export default function PublicNavigation({
  showRecruiterLink,
  onRecruiterClick,
}: PublicNavigationProps) {
  const { loginWithRedirect } = useAuth0();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navRef = useRef<HTMLElement>(null);

  const handleSignIn = () => {
    loginWithRedirect({
      authorizationParams: {
        audience: "https://veltus.au.auth0.com/api/v2/",
        scope: "openid profile email roles",
      },
    });
  };

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => {
      setActiveSection(null);
    }, 150);
  }, [cancelClose]);

  const handleNavEnter = useCallback(
    (sectionId: string) => {
      cancelClose();
      setActiveSection(sectionId);
    },
    [cancelClose]
  );

  const handleNavLeave = useCallback(() => {
    scheduleClose();
  }, [scheduleClose]);

  const handlePanelEnter = useCallback(() => {
    cancelClose();
  }, [cancelClose]);

  const handlePanelLeave = useCallback(() => {
    scheduleClose();
  }, [scheduleClose]);

  // Close on route change
  useEffect(() => {
    setActiveSection(null);
    setMobileOpen(false);
  }, [location.pathname]);

  // Close on Escape key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setActiveSection(null);
        setMobileOpen(false);
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  const activeSectionData = navSections.find((s) => s.id === activeSection);

  return (
    <>
      <nav ref={navRef} className="fixed top-0 left-0 right-0 z-50">
        {/* Main bar */}
        <div
          className={`bg-white/90 backdrop-blur-xl border-b transition-colors duration-200 ${
            activeSection
              ? "border-gray-200"
              : "border-gray-200/60"
          }`}
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex items-center justify-between h-12">
              {/* Logo */}
              <Link
                to="/"
                className="flex items-center gap-1.5 group"
                onMouseEnter={() => setActiveSection(null)}
              >
                <span className="text-xl font-semibold text-gray-900 tracking-tight">
                  OnSite
                </span>
              </Link>

              {/* Desktop nav items */}
              <div className="hidden lg:flex items-center gap-0.5">
                {navSections.map((section) => (
                  <button
                    key={section.id}
                    onMouseEnter={() => handleNavEnter(section.id)}
                    onMouseLeave={handleNavLeave}
                    onClick={() =>
                      setActiveSection(
                        activeSection === section.id ? null : section.id
                      )
                    }
                    className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200 ${
                      activeSection === section.id
                        ? "text-gray-900 bg-gray-100"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    {section.label}
                  </button>
                ))}

                {/* For Recruiters link - only shown on landing page */}
                {showRecruiterLink && (
                  <>
                    <div className="w-px h-4 bg-gray-200 mx-2" />
                    <button
                      onClick={onRecruiterClick}
                      onMouseEnter={() => setActiveSection(null)}
                      className="px-3 py-1.5 text-xs font-medium rounded-full text-gray-600 hover:text-gray-900 transition-all duration-200 inline-flex items-center gap-1.5"
                    >
                      For Recruiters
                      <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-amber-100 text-amber-700 border border-amber-200 leading-none">Beta</span>
                    </button>
                  </>
                )}
              </div>

              {/* Right side */}
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleSignIn}
                  size="sm"
                  className="hidden sm:inline-flex bg-gray-900 hover:bg-gray-800 text-white rounded-full text-xs font-medium px-4 h-8"
                >
                  Get Started
                </Button>
                {/* Mobile hamburger */}
                <button
                  onClick={() => {
                    setMobileOpen(!mobileOpen);
                    setActiveSection(null);
                  }}
                  className="lg:hidden p-1.5 text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100 transition-colors"
                  aria-label={mobileOpen ? "Close menu" : "Open menu"}
                >
                  {mobileOpen ? (
                    <X className="w-5 h-5" />
                  ) : (
                    <Menu className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop mega menu panel */}
        <AnimatePresence>
          {activeSection && activeSectionData && (
            <motion.div
              key={activeSection}
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="hidden lg:block bg-white/95 backdrop-blur-xl border-b border-gray-200 shadow-sm"
              onMouseEnter={handlePanelEnter}
              onMouseLeave={handlePanelLeave}
            >
              <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
                <div className="grid grid-cols-3 gap-1">
                  {activeSectionData.links.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`group flex items-start gap-3 rounded-xl p-3 transition-all duration-150 ${
                        location.pathname === link.path
                          ? "bg-gray-100"
                          : "hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span
                            className={`text-sm font-medium ${
                              location.pathname === link.path
                                ? "text-gray-900"
                                : "text-gray-800 group-hover:text-gray-900"
                            }`}
                          >
                            {link.label}
                          </span>
                          <ChevronRight className="w-3 h-3 text-gray-400 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-150" />
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                          {link.description}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Backdrop overlay for desktop */}
      <AnimatePresence>
        {activeSection && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="hidden lg:block fixed inset-0 bg-black/10 z-40"
            onClick={() => setActiveSection(null)}
          />
        )}
      </AnimatePresence>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden fixed inset-0 z-40"
          >
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/20 backdrop-blur-sm"
              onClick={() => setMobileOpen(false)}
            />

            {/* Panel */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
              className="absolute right-0 top-0 h-full w-full max-w-sm bg-white shadow-2xl overflow-y-auto"
            >
              {/* Mobile header */}
              <div className="flex items-center justify-between px-6 h-12 border-b border-gray-100">
                <span className="text-lg font-semibold text-gray-900 tracking-tight">
                  OnSite
                </span>
                <button
                  onClick={() => setMobileOpen(false)}
                  className="p-1.5 text-gray-500 hover:text-gray-900 rounded-full hover:bg-gray-100"
                  aria-label="Close menu"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="px-4 py-4">
                {/* For Recruiters link for mobile */}
                {showRecruiterLink && (
                  <button
                    onClick={() => {
                      setMobileOpen(false);
                      onRecruiterClick?.();
                    }}
                    className="w-full flex items-center justify-between px-3 py-3 mb-1 text-sm font-medium text-gray-900 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <span className="inline-flex items-center gap-2">
                      For Recruiters
                      <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-amber-100 text-amber-700 border border-amber-200 leading-none">Beta</span>
                    </span>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </button>
                )}

                {/* Nav sections */}
                <div className="space-y-1">
                  {navSections.map((section) => (
                    <div key={section.id}>
                      <button
                        onClick={() =>
                          setMobileExpanded(
                            mobileExpanded === section.id
                              ? null
                              : section.id
                          )
                        }
                        className="w-full flex items-center justify-between px-3 py-3 text-sm font-medium text-gray-900 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        {section.label}
                        <ChevronRight
                          className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                            mobileExpanded === section.id
                              ? "rotate-90"
                              : ""
                          }`}
                        />
                      </button>

                      <AnimatePresence>
                        {mobileExpanded === section.id && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{
                              duration: 0.2,
                              ease: "easeInOut",
                            }}
                            className="overflow-hidden"
                          >
                            <div className="pb-2 pl-3">
                              {section.links.map((link) => (
                                <Link
                                  key={link.path}
                                  to={link.path}
                                  onClick={() => setMobileOpen(false)}
                                  className={`block rounded-lg px-3 py-2.5 transition-colors ${
                                    location.pathname === link.path
                                      ? "bg-gray-100"
                                      : "hover:bg-gray-50"
                                  }`}
                                >
                                  <span
                                    className={`text-sm ${
                                      location.pathname === link.path
                                        ? "font-medium text-gray-900"
                                        : "text-gray-700"
                                    }`}
                                  >
                                    {link.label}
                                  </span>
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    {link.description}
                                  </p>
                                </Link>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>

                {/* Mobile CTA */}
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <Button
                    onClick={() => {
                      setMobileOpen(false);
                      handleSignIn();
                    }}
                    className="w-full bg-gray-900 hover:bg-gray-800 text-white rounded-lg text-sm font-medium h-10"
                  >
                    Get Started
                  </Button>
                  <button
                    onClick={() => {
                      setMobileOpen(false);
                      handleSignIn();
                    }}
                    className="w-full mt-2 py-2.5 text-sm text-gray-600 hover:text-gray-900 text-center transition-colors"
                  >
                    Sign in
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
